import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Clock,
  Download,
  FileDown,
  Hash,
  Headset,
  LineChart,
  MessageSquare,
  Moon,
  Palette,
  PenLine,
  Search,
  Sun,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useTheme } from "../ThemeContext";
import { authLinkClass } from "./AuthLayout";

// Product-branded Login / Signup screens linked from the igr-consulting.com landing page.
// Only the surrounding UI changes per product; the auth forms are passed in as children.

const LANDING_URL = "https://igr-consulting.com";
const WEBSITE_URL = "https://www.infiniumglobalresearch.com";
const TERMS_URL = `${WEBSITE_URL}/terms-condition`;
const PRIVACY_URL = `${WEBSITE_URL}/privacy-policy`;

export const PRODUCTS = {
  "market-researcher": {
    name: "Market Researcher",
    eyebrow: "Turn questions into opportunities",
    heading: (
      <>
        Market Insights for Confident <span className="text-[#0799ad] dark:text-cyan-400">Decisions</span>
      </>
    ),
    lead: "Get accurate, research-backed answers from Infinium’s global report database. Download full reports, create presentation slides and connect with our expert analysts for deeper insights.",
    signupIntro: "Start exploring market insights in minutes.",
    benefits: [
      { icon: Search, label: "Research-backed insights" },
      { icon: FileDown, label: "Download reports & presentations" },
      { icon: Users, label: "Get support from human analysts" },
      { icon: BarChart3, label: "Useful for strategy, investment and growth" },
    ],
    howTitle: "How Market Researcher Works",
    howSubtitle: "From your question to actionable insights — in just a few steps.",
    steps: [
      { icon: MessageSquare, title: "Ask Your Question", text: "Type your market question in simple language." },
      { icon: Search, title: "Get Research-Backed Insights", text: "Insights draw on Infinium’s global report database to help you make informed decisions." },
      { icon: Download, title: "Download & Use", text: "Download reports or presentation slides for your meetings and strategy." },
      { icon: Headset, title: "Get Human Support", text: "Need deeper insights or a customised report? Connect with our expert analysts." },
    ],
    footerTag: "Research in Minutes, Value for Lifetime",
  },
  "igr-creatives": {
    name: "IGR Creatives",
    eyebrow: "Turn insights into impact",
    heading: (
      <>
        Create. Schedule. <span className="text-[#0799ad] dark:text-cyan-400">Grow Faster.</span>
      </>
    ),
    lead: "Turn market insights into high-performing marketing content. IGR Creatives helps you create on-brand social posts, plan campaigns, track performance and discover what’s trending — all in one place.",
    signupIntro: "Start creating high-performing content with IGR Creatives.",
    benefits: [
      { icon: Zap, label: "Content creation made easier" },
      { icon: CalendarDays, label: "Schedule across platforms" },
      { icon: BarChart3, label: "Track performance and get insights" },
      { icon: Hash, label: "Discover trends and campaign ideas" },
    ],
    howTitle: "How IGR Creatives Works",
    howSubtitle: "From market insights to measurable marketing impact — in a few simple steps.",
    steps: [
      { icon: Search, title: "Get Insights", text: "Start with insights from Infi-portal Market Researcher or your own brief." },
      { icon: PenLine, title: "Create Content", text: "Develop tailored content ideas, captions and creatives based on trends and your industry." },
      { icon: CalendarDays, title: "Schedule & Publish", text: "Plan and schedule posts across your supported social media platforms." },
      { icon: LineChart, title: "Track & Improve", text: "Monitor performance, discover what’s trending and use recommendations to improve campaigns." },
    ],
    strip: [
      { icon: Clock, title: "Save Time", text: "Get from idea to publish-ready content faster." },
      { icon: TrendingUp, title: "Stay Relevant", text: "Use trend signals and industry hashtags." },
      { icon: Palette, title: "Consistent Brand Voice", text: "Keep content aligned across campaigns." },
      { icon: BarChart3, title: "Improve Performance", text: "Track results and make smarter adjustments." },
    ],
    footerTag: "Research in Minutes, Value for Lifetime",
  },
};

export const PRODUCT_SLUGS = Object.keys(PRODUCTS);

const btnBase =
  "inline-flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 sm:py-3 rounded-[10px] border text-xs sm:text-[13px] font-bold whitespace-nowrap transition-all duration-200 hover:-translate-y-px hover:shadow-[0_8px_20px_#08779d20]";
const btnPrimary = `${btnBase} border-transparent text-white bg-gradient-to-r from-[#0799ad] to-[#08779d]`;
const btnOutline = `${btnBase} border-[#b8d3df] bg-white text-[#102044] dark:border-gray-700 dark:bg-gray-900 dark:text-white`;

const iconTileClass =
  "grid place-items-center rounded-[14px] border border-[#c5edf4] bg-[#d8f4f9] text-[#087c9f] dark:border-cyan-900/60 dark:bg-cyan-950/50 dark:text-cyan-400";

const skeleton = (width) => (
  <div className={`h-[5px] my-1.5 rounded-lg bg-[#dce9ef] dark:bg-gray-700 ${width}`}></div>
);

const Bars = ({ heights, className }) => (
  <div className={`flex items-end gap-[5px] ${className}`}>
    {heights.map((h, i) => (
      <i
        key={i}
        className="block flex-1 rounded-t-[3px] bg-gradient-to-b from-[#4cc8e2] to-[#087aa4]"
        style={{ height: `${h}%` }}
      ></i>
    ))}
  </div>
);

const ScreenHead = ({ name }) => (
  <div className="flex items-center justify-between pb-2.5 border-b border-[#e5eff3] text-[10px] font-extrabold dark:border-gray-800">
    <div>
      Infi-<span className="text-[#0799ad]">portal</span> &nbsp;|&nbsp; {name}
    </div>
    <span className="text-[#0799ad]">● Online</span>
  </div>
);

const ScreenQuestion = ({ children, className = "" }) => (
  <div className={`flex items-center gap-2.5 p-2.5 rounded-[10px] border border-[#e4eff3] bg-white text-[10px] leading-[1.4] text-[#314c69] dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 ${className}`}>
    <div className="flex-1">{children}</div>
    <div className="grid place-items-center w-7 h-7 shrink-0 rounded-full bg-[#0799ad] text-white">
      <ArrowRight className="w-4 h-4" />
    </div>
  </div>
);

const Laptop = ({ className, children }) => (
  <div
    className={`relative z-[1] rounded-t-[18px] rounded-b-[9px] border-8 border-b-[12px] border-[#112d3b] bg-white shadow-[0_22px_40px_#083d522c] [transform:perspective(900px)_rotateY(-3deg)] after:content-[''] after:absolute after:-bottom-[21px] after:-left-[4%] after:w-[108%] after:h-3 after:rounded-t-sm after:rounded-b-xl after:bg-gradient-to-b after:from-[#aabec8] after:to-[#eaf1f4] ${className}`}
  >
    <div className="p-3 rounded-md bg-[#fbfeff] text-[#102044] dark:bg-gray-950 dark:text-white">{children}</div>
  </div>
);

const MarketResearcherVisual = () => (
  <div className="relative min-w-0 py-[26px]">
    <div className="absolute right-0 top-0 w-[70%] h-[54%] p-[18px] rounded-xl -rotate-[7deg] border border-[#e3f0f3] bg-white/90 shadow-[0_12px_30px_#073a5022] dark:border-gray-800 dark:bg-gray-900/90">
      <strong className="block max-w-[160px] text-xs leading-[1.4]">Global Plant-Based Protein Market 2025–2032</strong>
      <small className="block mt-2 text-[9px] text-[#526784] dark:text-gray-400">Market forecast &amp; regional outlook</small>
      <div className="h-[38px] mt-3 rounded-md bg-gradient-to-br from-[#e5f5f8] to-[#b5e1ea] dark:from-gray-800 dark:to-cyan-900/60"></div>
      <Bars heights={[25, 36, 42, 57, 70, 83, 100]} className="h-[35px] mt-2 !gap-1" />
    </div>
    <Laptop className="mt-[82px]">
      <ScreenHead name="Market Researcher" />
      <ScreenQuestion className="my-3">What is the market size and growth outlook for my industry?</ScreenQuestion>
      <div className="p-[11px] rounded-[10px] border border-[#e4eff3] bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center gap-1.5 mb-2 text-[10px] font-extrabold">
          <BarChart3 className="w-3 h-3" /> Market Overview
        </div>
        {skeleton("w-3/4")}
        {skeleton("w-[58%]")}
        {skeleton("w-[39%]")}
        <Bars heights={[23, 30, 38, 47, 57, 69, 80, 100]} className="h-[62px] mt-[7px] px-1" />
      </div>
      <div className="grid grid-cols-3 gap-1.5 mt-[9px]">
        {[
          ["PDF ↓", "Download Full Report"],
          ["PPT ↓", "Download Presentation"],
          ["♙", "Talk to an Analyst"],
        ].map(([badge, label]) => (
          <div
            key={label}
            className="px-1 py-2 rounded-[7px] border border-[#e0edf1] bg-white text-center text-[8px] leading-[1.35] text-[#294a67] dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300"
          >
            <b className="block mb-[3px] text-[10px] text-[#0799ad]">{badge}</b>
            {label}
          </div>
        ))}
      </div>
    </Laptop>
  </div>
);

const POSTERS = [
  { text: ["GOOD FOOD", "BRIGHTER", "TOMORROW"], symbol: "✦", className: "left-[12%] top-0 -rotate-[9deg] bg-gradient-to-br from-[#102b36] to-[#0b7c8d] text-white" },
  { text: ["PLANT POWER", "EVERY DAY"], symbol: "✧", className: "left-[37%] -top-[9px] rotate-2 bg-gradient-to-br from-[#07523f] to-[#24a68d] text-white" },
  { text: ["Small Choices", "Big Change"], symbol: "↗", className: "right-[2%] top-[18px] rotate-[7deg] bg-gradient-to-br from-[#f6fbfc] to-[#9bdde5] text-[#104057]" },
];

const POSTS = [
  { text: "GOOD FOOD BRIGHTER TOMORROW", symbol: "✦", className: "from-[#d5f4f4] to-[#14899b] text-white" },
  { text: "PLANT POWER EVERY DAY", symbol: "✧", className: "from-[#dff8ed] to-[#168c71] text-white" },
  { text: "SMALL CHOICES BIG CHANGE", symbol: "↗", className: "from-[#e9fbfc] to-[#6ac9d7] text-[#15455a]" },
];

const CreativesVisual = () => (
  <div className="relative min-w-0 py-[25px]">
    {POSTERS.map((poster) => (
      <div
        key={poster.symbol}
        className={`absolute w-[30%] h-[43%] flex flex-col justify-between px-2.5 py-[13px] rounded-[10px] border border-white/70 text-[11px] font-extrabold leading-[1.25] shadow-[0_12px_25px_#073a5022] ${poster.className}`}
      >
        <div>
          {poster.text.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
        <span className="self-end text-[23px]">{poster.symbol}</span>
      </div>
    ))}
    <Laptop className="mt-[100px]">
      <ScreenHead name="IGR Creatives" />
      <div className="grid grid-cols-[70px_1fr] gap-2.5 pt-2.5">
        <div className="flex flex-col gap-3 text-[9px] text-[#08779d] dark:text-cyan-400">
          {["⌂ Home", "✎ Create", "▦ Schedule", "▥ Analytics", "# Trends", "◇ Brand Kit"].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div className="min-w-0">
          <ScreenQuestion className="mb-2.5">Create engaging content for your audience and industry</ScreenQuestion>
          <div className="mb-[9px] p-[9px] rounded-[9px] border border-[#e4eff3] bg-white dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-2 text-[10px] font-extrabold">Social media posts</div>
            <div className="grid grid-cols-3 gap-[5px]">
              {POSTS.map((post) => (
                <div
                  key={post.symbol}
                  className={`min-h-[65px] flex flex-col justify-between px-[5px] py-[7px] rounded-md bg-gradient-to-br text-[8px] font-extrabold ${post.className}`}
                >
                  {post.text}
                  <b className="text-[17px]">{post.symbol}</b>
                </div>
              ))}
            </div>
          </div>
          <div className="p-[9px] rounded-[9px] border border-[#e4eff3] bg-white dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-2 text-[10px] font-extrabold">Content schedule →</div>
            <div className="grid grid-cols-5 gap-1">
              {["Mon", "Tue", "Wed", "Thu", "Fri"].map((day) => (
                <div
                  key={day}
                  className="px-0.5 py-1.5 rounded-md bg-[#edf9fb] text-center text-[8px] text-[#365a75] dark:bg-gray-800 dark:text-gray-300"
                >
                  <strong className="block mb-1 text-[#0799ad]">{day}</strong>Post
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Laptop>
  </div>
);

const VISUALS = {
  "market-researcher": MarketResearcherVisual,
  "igr-creatives": CreativesVisual,
};

const ProductAuthLayout = ({ product, title, subtitle, cardFooter, children }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const config = PRODUCTS[product];
  const Visual = VISUALS[product];

  return (
    <div className="min-h-screen bg-white text-[#102044] font-['Inter',ui-sans-serif,system-ui,sans-serif] antialiased dark:bg-gray-950 dark:text-white">
      {/* Header */}
      <header className="min-h-[72px] sm:min-h-[78px] flex items-center justify-between gap-3 sm:gap-6 px-[18px] sm:px-[clamp(20px,4.5vw,70px)] py-3 border-b border-[#eaf1f4] bg-white/95 dark:border-gray-800 dark:bg-gray-950/95">
        <a href={LANDING_URL} className="flex items-center gap-2 sm:gap-[13px] min-w-0" aria-label="Infinium Global Research home">
          <img
            src="/logo.png"
            alt="Infinium Global Research"
            className="block h-[38px] sm:h-[48px] w-auto max-w-[150px] sm:max-w-[230px] object-contain shrink-0"
          />
          <span className="hidden min-[390px]:block h-7 sm:h-[34px] w-px bg-[#d7e6eb] dark:bg-gray-700"></span>
          <span className="hidden min-[390px]:block text-xs sm:text-[15px] font-bold whitespace-nowrap">{config.name}</span>
        </a>

        <nav className="hidden xl:flex items-center gap-[clamp(14px,2vw,30px)] text-[13px] text-[#334760] dark:text-gray-300" aria-label="Main navigation">
          <a href="#benefits" className="hover:text-[#0799ad] transition-colors">What You Get</a>
          <a href="#how-it-works" className="hover:text-[#0799ad] transition-colors">How It Works</a>
          <a href="#support" className="hover:text-[#0799ad] transition-colors">Support</a>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2.5">
          <button
            type="button"
            onClick={toggleTheme}
            className="p-2.5 rounded-[10px] text-[#334760] hover:bg-[#eaf9fc] dark:text-yellow-400 dark:hover:bg-gray-800 transition-colors"
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            aria-label={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {isDarkMode ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
          </button>
          <Link to={`/${product}/login`} className={btnOutline}>
            Sign In
          </Link>
          <Link to={`/${product}/signup`} className={btnPrimary}>
            Sign Up
          </Link>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="product-auth-hero relative overflow-hidden px-[18px] sm:px-[clamp(20px,4.5vw,70px)] pt-8 sm:pt-[42px] pb-[30px] sm:pb-[34px]">
          <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(360px,.82fr)] xl:grid-cols-[minmax(0,1.05fr)_minmax(300px,.88fr)_minmax(360px,.72fr)] items-center gap-6 lg:gap-[25px] lg:min-h-[460px]">
            {/* Copy */}
            <div className="relative z-[1] lg:py-2.5">
              <p className="m-0 mb-[18px] text-[10px] sm:text-[11px] tracking-[.19em] font-extrabold uppercase text-[#3f6480] dark:text-cyan-400">
                {config.eyebrow}
              </p>
              <h1 className="m-0 max-w-[620px] text-[clamp(38px,10vw,54px)] sm:text-[clamp(36px,4.05vw,60px)] leading-[1.05] tracking-[-1.6px] sm:tracking-[-2.1px] font-extrabold">
                {config.heading}
              </h1>
              <p className="max-w-[560px] mt-[18px] mb-[26px] text-[15px] lg:text-[clamp(15px,1.25vw,18px)] leading-[1.6] text-[#405977] dark:text-gray-400">
                {config.lead}
              </p>
              <div id="benefits" className="grid grid-cols-2 min-[390px]:grid-cols-4 gap-x-2 gap-y-4 sm:gap-3 max-w-[600px]">
                {config.benefits.map(({ icon: Icon, label }) => (
                  <div key={label} className="text-center text-[10px] sm:text-[11px] leading-[1.45] text-[#314e6c] dark:text-gray-300">
                    <div className={`${iconTileClass} mx-auto mb-[9px] w-[43px] h-[43px] sm:w-[49px] sm:h-[49px]`}>
                      <Icon className="w-[22px] h-[22px]" />
                    </div>
                    {label}
                  </div>
                ))}
              </div>
            </div>

            {/* Product preview */}
            <div className="hidden xl:block" aria-hidden="true">
              <Visual />
            </div>

            {/* Form card */}
            <aside
              aria-labelledby="auth-card-title"
              className="relative z-[2] w-full lg:max-w-[480px] lg:justify-self-end px-[19px] sm:px-[23px] pt-[23px] sm:pt-[25px] pb-[21px] rounded-[20px] border border-[#e6f0f3] bg-white/95 shadow-[0_22px_55px_#073e5424] dark:border-gray-800 dark:bg-gray-900/95"
            >
              <h2 id="auth-card-title" className="m-0 mb-[7px] text-[22px] sm:text-[23px] tracking-[-.7px] font-extrabold">
                {title}
              </h2>
              <p className="m-0 mb-[18px] text-[13px] leading-normal text-[#526784] dark:text-gray-400">{subtitle}</p>

              {children}

              {cardFooter && (
                <p className="m-0 mt-4 text-center text-xs text-[#526784] dark:text-gray-400">{cardFooter}</p>
              )}
            </aside>
          </div>
        </section>

        {/* How it works */}
        <section
          id="how-it-works"
          className="px-[18px] sm:px-[clamp(20px,4.5vw,70px)] pt-8 sm:pt-[34px] pb-8 sm:pb-[45px] bg-gradient-to-b from-white to-[#f3fcfd] dark:from-gray-950 dark:to-gray-900"
        >
          <div className="mx-auto mb-7 text-center">
            <h2 className="m-0 text-[clamp(25px,2.6vw,35px)] tracking-[-1px] font-extrabold">{config.howTitle}</h2>
            <p className="mt-2 mb-0 text-[13px] sm:text-[15px] text-[#526784] dark:text-gray-400">{config.howSubtitle}</p>
          </div>
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-[17px] sm:gap-[15px]">
            {config.steps.map(({ icon: Icon, title: stepTitle, text }, index) => (
              <article
                key={stepTitle}
                id={index === config.steps.length - 1 ? "support" : undefined}
                className="relative flex items-start gap-[13px] min-h-[100px] sm:min-h-[124px] p-[18px] sm:px-[17px] sm:py-[22px] rounded-[17px] border border-[#e7f1f4] bg-white/90 shadow-[0_8px_24px_#0b66800a] dark:border-gray-800 dark:bg-gray-900/80"
              >
                <span className="absolute -top-3 left-4 grid place-items-center w-[30px] h-[30px] rounded-full bg-gradient-to-br from-[#31cbd0] to-[#087aa4] text-[13px] font-extrabold text-white">
                  {index + 1}
                </span>
                <div className={`${iconTileClass} mt-[7px] w-12 h-12 shrink-0 !rounded-xl !border-0`}>
                  <Icon className="w-[22px] h-[22px]" />
                </div>
                <div>
                  <h3 className="mt-[9px] mb-[7px] text-[13px] font-bold">{stepTitle}</h3>
                  <p className="m-0 text-xs leading-normal text-[#526784] dark:text-gray-400">{text}</p>
                </div>
              </article>
            ))}
          </div>

          {config.strip && (
            <div className="max-w-[1400px] mx-auto mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {config.strip.map(({ icon: Icon, title: stripTitle, text }) => (
                <div
                  key={stripTitle}
                  className="flex items-center gap-3 p-3.5 rounded-[13px] border border-[#e5f0f3] bg-white/80 dark:border-gray-800 dark:bg-gray-900/80"
                >
                  <div className={`${iconTileClass} w-[42px] h-[42px] shrink-0 !rounded-[11px] !border-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <strong className="block text-xs">{stripTitle}</strong>
                    <p className="mt-1 mb-0 text-[11px] leading-[1.4] text-[#526784] dark:text-gray-400">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-5 p-5 border-t border-[#e8f1f4] text-[11px] text-[#71839a] dark:border-gray-800 dark:text-gray-500">
        <p className="m-0">
          © {new Date().getFullYear()} Infi-portal · {config.name} · {config.footerTag}
        </p>
        <nav className="flex gap-[18px]" aria-label="Legal links">
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={authLinkClass}>
            Terms
          </a>
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={authLinkClass}>
            Privacy
          </a>
        </nav>
      </footer>
    </div>
  );
};

export default ProductAuthLayout;
